import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  Button,
  Modal,
  Alert
} from "react-bootstrap";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";
import EditTenant from "./EditTenant";
import * as XLSX from "xlsx";

const ManageTenant = () => {
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState("");
  const [editingTenant, setEditingTenant] = useState(null);

  // 🔹 For confirmation modal + alerts
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState("success");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          setError("Authorization token not found.");
          return;
        }

        const response = await axios.get("https://housys.skymarkone.in:8001/api/getalloffices", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && response.data.status === "success") {
          setTenants(response.data.office || []);
        } else {
          setError(response.data.message || "Failed to fetch data.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("An error occurred while fetching data.");
      }
    };

    fetchData();
  }, [token]);

  const handleEditClick = (tenant) => {
    setEditingTenant(tenant);
  };

  // 🔹 Show confirmation modal
  const confirmDelete = (tenant) => {
    setTenantToDelete(tenant);
    setShowDeleteModal(true);
  };

  // 🔹 Delete tenant after confirmation
  const handleDeleteConfirmed = async () => {
    if (!tenantToDelete) return;

    try {
      const response = await axios.post(
        "https://housys.skymarkone.in:8001/api/delete-office",
        { company_name: tenantToDelete.company_name },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        setTenants((prevTenants) =>
          prevTenants.filter((t) => t.company_name !== tenantToDelete.company_name)
        );
        setAlertVariant("success");
        setAlertMessage(`✅ ${tenantToDelete.company_name} deleted successfully.`);
      } else {
        setAlertVariant("danger");
        setAlertMessage(response.data.message || "❌ Failed to delete tenant.");
      }
    } catch (error) {
      console.error("Error deleting tenant:", error);
      setAlertVariant("danger");
      setAlertMessage("❌ An error occurred while deleting the tenant.");
    } finally {
      setShowDeleteModal(false);
      setTenantToDelete(null);
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTenantToDelete(null);
  };

  const handleSave = (updatedTenant) => {
    setTenants((prevTenants) =>
      prevTenants.map((tenant) =>
        tenant.o_id === updatedTenant.o_id ? updatedTenant : tenant
      )
    );
  };

  // 🔹 Excel Export
  const handleDownloadExcel = () => {
    if (tenants.length === 0) {
      setAlertVariant("warning");
      setAlertMessage("⚠️ No data available to export.");
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    const columns = ["Tenant Name", "Car Capacity", "Bike Capacity"];
    const rows = tenants.map((tenant) => [
      tenant.company_name || "N/A",
      tenant.car_capacity || "N/A",
      tenant.bike_capacity || "N/A",
    ]);

    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);

    ws['!cols'] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, "Tenants Data");

    const now = new Date();
    const dateTime = now.toISOString().slice(0, 19).replace(/[:]/g, '-').replace('T', '_');
    const fileName = `Tenants_Data_${dateTime}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Container fluid className="p-4" style={{ minHeight: "100vh" }}>
      {/* 🔹 Alert Notification */}
      {alertMessage && (
        <Alert variant={alertVariant} className="text-center fw-bold">
          {alertMessage}
        </Alert>
      )}

      <Row className="mb-3">
        <Col xs={12} lg={8}>
          <h2 className="fw-bold">Tenant Management</h2>
          <p className="text-muted">All registered tenant details</p>
        </Col>
        <Col xs={12} lg={4} className="d-flex justify-content-lg-end align-items-center">
          <Button
            variant="success"
            onClick={handleDownloadExcel}
            disabled={tenants.length === 0}
          >
            Export to Excel <i className="bi bi-download ms-1"></i>
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <strong>TOP 100 RECORDS</strong>
            </Card.Header>
            <Card.Body>
              {error && <p className="text-danger">{error}</p>}
              <div style={{ overflowX: "auto" }}>
                <Table bordered hover responsive className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Tenant Name</th>
                      <th>Car Capacity</th>
                      <th>Bike Capacity</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.length > 0 ? (
                      tenants.map((tenant, index) => (
                        <tr key={tenant.o_id}>
                          <td>{index + 1}</td>
                          <td>{tenant.company_name || "N/A"}</td>
                          <td>{tenant.car_capacity || "N/A"}</td>
                          <td>{tenant.bike_capacity || "N/A"}</td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="text-primary p-0 me-2"
                              onClick={() => handleEditClick(tenant)}
                            >
                              <i className="bi bi-pencil-square fs-5"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => confirmDelete(tenant)}
                            >
                              <i className="bi bi-trash fs-5"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 🔹 Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete tenant{" "}
          <strong>{tenantToDelete?.company_name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {editingTenant && (
        <EditTenant
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
};

export default ManageTenant;
